import { normalizeDecimal, parseDecimal, scaledToString } from './decimal';

/**
 * ISO 4217 minor-unit exponents. Mirrors BillerAPI `App\Support\Money\Currency`
 * so minor units mean the same thing on both sides; unknown codes fall back
 * to Intl's notion of the currency's fraction digits.
 */
const EXPONENTS: Readonly<Record<string, number>> = {
  AED: 2, AUD: 2, BHD: 3, BRL: 2, CAD: 2, CHF: 2,
  CLP: 0, CNY: 2, CZK: 2, DKK: 2, EUR: 2, GBP: 2,
  HKD: 2, HUF: 2, IDR: 2, ILS: 2, INR: 2, ISK: 0,
  JOD: 3, JPY: 0, KRW: 0, KWD: 3, MXN: 2, MYR: 2,
  NOK: 2, NZD: 2, OMR: 3, PHP: 2, PLN: 2, SAR: 2,
  SEK: 2, SGD: 2, THB: 2, TND: 3, TRY: 2, TWD: 2,
  UGX: 0, USD: 2, VND: 0, XAF: 0, XOF: 0, ZAR: 2,
}; // prettier-ignore

export const SUPPORTED_CURRENCIES: readonly string[] = Object.keys(EXPONENTS);

export function currencyExponent(currency: string): number {
  const code = currency.toUpperCase();
  const known = EXPONENTS[code];
  if (known !== undefined) return known;
  try {
    return (
      new Intl.NumberFormat('en', { style: 'currency', currency: code }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/** Integer minor units → exact decimal string in major units ("123456" CAD → "1234.56"). */
export function minorToDecimal(minor: number | bigint, currency: string): string {
  const exponent = currencyExponent(currency);
  if (typeof minor === 'number' && !Number.isSafeInteger(minor)) {
    throw new RangeError(`Minor units must be a safe integer, got ${minor}`);
  }
  return scaledToString(BigInt(minor), exponent, exponent);
}

export type MinorParseResult =
  { ok: true; minor: number } | { ok: false; reason: 'invalid' | 'precision' | 'negative' | 'range' };

/**
 * A user-entered major amount → integer minor units, exactly. More decimals
 * than the currency allows is an error, not a rounding.
 */
export function parseMajorToMinor(
  input: string,
  currency: string,
  { allowNegative = false }: { allowNegative?: boolean } = {},
): MinorParseResult {
  const parsed = parseDecimal(input.replace(/[\s,]/g, ''));
  if (!parsed) return { ok: false, reason: 'invalid' };
  const exponent = currencyExponent(currency);
  // Trailing zeros beyond the exponent are harmless ("12.500" CAD).
  const normalized = parseDecimal(normalizeDecimal(input.replace(/[\s,]/g, '')));
  if (!normalized || normalized.scale > exponent) return { ok: false, reason: 'precision' };
  if (normalized.negative && !allowNegative) return { ok: false, reason: 'negative' };
  const scaled = normalized.digits * 10n ** BigInt(exponent - normalized.scale);
  const signed = normalized.negative ? -scaled : scaled;
  if (signed > BigInt(Number.MAX_SAFE_INTEGER) || signed < BigInt(Number.MIN_SAFE_INTEGER)) {
    return { ok: false, reason: 'range' };
  }
  return { ok: true, minor: Number(signed) };
}

export type CurrencyDisplay = 'symbol' | 'narrowSymbol' | 'code' | 'none';

export interface FormatMoneyOptions {
  locale?: string | undefined;
  display?: CurrencyDisplay;
  /** "1.2M"-style compact notation for KPI tiles. */
  compact?: boolean;
  signDisplay?: 'auto' | 'always' | 'exceptZero' | 'negative' | 'never';
}

const formatterCache = new Map<string, Intl.NumberFormat>();

/**
 * The tenant's default currency renders with its narrow symbol ("$4,065.25"
 * for CAD in a CAD tenant); every other currency keeps a disambiguating
 * symbol ("US$39.00"). Set from the session.
 */
let homeCurrency: string | null = null;

export function setHomeCurrency(currency: string | null): void {
  homeCurrency = currency ? currency.toUpperCase() : null;
}

const symbolCache = new Map<string, string>();

function symbolOf(currency: string, display: 'symbol' | 'narrowSymbol', locale?: string): string {
  const key = `${locale ?? ''}|${currency}|${display}`;
  let symbol = symbolCache.get(key);
  if (symbol === undefined) {
    try {
      symbol =
        new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: display })
          .formatToParts(0)
          .find((p) => p.type === 'currency')?.value ?? currency;
    } catch {
      symbol = currency;
    }
    symbolCache.set(key, symbol);
  }
  return symbol;
}

/**
 * Home currency → its narrow symbol. Any other currency → its symbol, unless
 * that symbol would read the same as the home one (USD "$" next to CAD "$" in
 * an en-US browser), in which case the ISO code is shown instead.
 */
function resolveDisplay(
  currency: string,
  display: CurrencyDisplay | undefined,
  locale?: string,
): CurrencyDisplay {
  if (display) return display;
  if (!homeCurrency || homeCurrency === currency)
    return homeCurrency === currency ? 'narrowSymbol' : 'symbol';
  return symbolOf(currency, 'symbol', locale) === symbolOf(homeCurrency, 'narrowSymbol', locale)
    ? 'code'
    : 'symbol';
}

function formatter(currency: string, exponent: number, options: FormatMoneyOptions): Intl.NumberFormat {
  const display = resolveDisplay(currency, options.display, options.locale);
  const key = [
    options.locale ?? '',
    currency,
    exponent,
    display,
    options.compact ? 1 : 0,
    options.signDisplay ?? '',
  ].join('|');
  let nf = formatterCache.get(key);
  if (!nf) {
    const base: Intl.NumberFormatOptions =
      display === 'none' ? { style: 'decimal' } : { style: 'currency', currency, currencyDisplay: display };
    nf = new Intl.NumberFormat(options.locale, {
      ...base,
      ...(options.compact
        ? { notation: 'compact', maximumFractionDigits: 1 }
        : { minimumFractionDigits: exponent, maximumFractionDigits: exponent }),
      ...(options.signDisplay ? { signDisplay: options.signDisplay } : {}),
    });
    formatterCache.set(key, nf);
  }
  return nf;
}

type NumericString = `${number}`;

/** Format integer minor units for display. Exact: Intl formats the decimal string, never a float. */
export function formatMoney(
  minor: number | bigint,
  currency: string,
  options: FormatMoneyOptions = {},
): string {
  const code = currency.toUpperCase();
  const exponent = currencyExponent(code);
  const decimal = minorToDecimal(minor, code) as NumericString;
  try {
    return formatter(code, exponent, options).format(decimal);
  } catch {
    return `${decimal} ${code}`;
  }
}

/** The formatted parts of an amount, for figures that style symbol, integer and fraction apart. */
export function formatMoneyParts(
  minor: number | bigint,
  currency: string,
  options: FormatMoneyOptions = {},
): Intl.NumberFormatPart[] {
  const code = currency.toUpperCase();
  const decimal = minorToDecimal(minor, code) as NumericString;
  try {
    return formatter(code, currencyExponent(code), options).formatToParts(decimal);
  } catch {
    return [{ type: 'literal', value: `${decimal} ${code}` }];
  }
}

/**
 * Format a decimal-string amount (a unit price such as "0.0025") keeping
 * its precision: at least the currency's digits, at most the value's own.
 */
export function formatDecimalMoney(
  amount: string,
  currency: string,
  options: Omit<FormatMoneyOptions, 'compact'> = {},
): string {
  const code = currency.toUpperCase();
  const exponent = currencyExponent(code);
  const normalized = normalizeDecimal(amount, exponent);
  const parsed = parseDecimal(normalized);
  if (!parsed) return amount;
  const digits = Math.min(Math.max(parsed.scale, exponent), 20);
  try {
    const display = resolveDisplay(code, options.display, options.locale);
    return new Intl.NumberFormat(options.locale, {
      ...(display === 'none'
        ? { style: 'decimal' }
        : { style: 'currency', currency: code, currencyDisplay: display }),
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      ...(options.signDisplay ? { signDisplay: options.signDisplay } : {}),
    }).format(normalized as NumericString);
  } catch {
    return `${normalized} ${code}`;
  }
}

/** Format a decimal-string quantity or rate ("150.000000" → "150", "1.5" → "1.5"). */
export function formatQuantity(value: string, locale?: string): string {
  const normalized = normalizeDecimal(value);
  const parsed = parseDecimal(normalized);
  if (!parsed) return value;
  try {
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: Math.min(parsed.scale, 20),
    }).format(normalized as NumericString);
  } catch {
    return normalized;
  }
}

/** "13.000000" → "13%", "14.975" → "14.975%" */
export function formatPercent(value: string, locale?: string): string {
  return `${formatQuantity(value, locale)}%`;
}

/** Exact sum of minor-unit integers. */
export function sumMinor(values: readonly (number | null | undefined)[]): number {
  let total = 0n;
  for (const value of values) {
    if (typeof value === 'number') total += BigInt(value);
  }
  return Number(total);
}

/** The currency symbol alone ("$", "€", "CA$"), for input adornments. */
export function currencySymbol(currency: string, locale?: string): string {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
}

/**
 * amount × part ÷ whole in integer minor units, rounded half away from zero
 * (BigDecimal HalfUp) — e.g. the tax that travels with a partial credit.
 */
export function prorateMinor(amount: number, part: number, whole: number): number {
  if (whole === 0) return 0;
  const numerator = BigInt(amount) * BigInt(part);
  const denominator = BigInt(whole);
  const quotient = numerator / denominator;
  const remainder = numerator % denominator;
  const abs = (x: bigint) => (x < 0n ? -x : x);
  if (2n * abs(remainder) < abs(denominator)) return Number(quotient);
  return Number(quotient + (numerator < 0n !== denominator < 0n ? -1n : 1n));
}
